
import { computed, ref, useContext, useRoute, useRouter } from '@nuxtjs/composition-api';
import useVariantDetails from '@/composable/useVariantDetails';
import usePrices from '~/composable/usePrices';
import useUtilities from '~/composable/useUtilities';
import useTracking from '~/composable/tracking/useTracking';
import useSearch from '~/composable/useSearch';
import useSavedUrls from '~/composable/useSavedUrls';
import MediaPopup from '@/components/Modal/MediaPopup.vue';

export default {
  name: 'ProductListCard',
  components: {
    MediaPopup
  },
  props: {
    product: {
      type: Object,
      default: () => {}
    },
    hasSpecialPrices: {
      type: Boolean,
      default: null
    },
    categoryTree: {
      type: Array,
      default: () => null
    },
    initialVisible: {
      type: Boolean,
      default: false
    }
  },
  setup (props) {
    const route = useRoute()
    const categorySlug = route.value.params.categoryslug.toLowerCase();
    const { formatPrice } = useUtilities()
    const { app } = useContext()
    const { getPricePerSku } = usePrices(app.$hubble)
    const productVariants = computed(() => props.product)
    const chosenColor = ref(null)
    const chosenStorage = ref(null)
    const variantDetails = useVariantDetails(productVariants, chosenColor, chosenStorage, false, getPricePerSku, undefined, null, categorySlug, app)
    const { track } = useTracking()
    const { state } = useSearch(app)
    const { saveBackUrl } = useSavedUrls()
    const router = useRouter()
    const {
      productName,
      masterProductLabels,
      masterProductTimer,
      productBrand,
      masterProductImages,
      productFamilyColorVariants,
      productStorageVariants,
      masterProductPrice,
      masterProductSlug,
      masterVariantPricingDetails,
      chosenVariantCashPrice,
      campaign,
      featureList,
      masterProduct
    } = variantDetails
    const loadProductDetails = (backUrl) => {
      if (!backUrl) {
        if (process.client) {
          console.warn('Card.vue: Cannot navigate - backUrl is null or undefined', { productBrand: productBrand?.value, masterProductSlug: masterProductSlug?.value, masterProduct: masterProduct?.value })
        }
        return
      }
      saveBackUrl(backUrl)
      router.push(backUrl)
    }
    const logUrlConstructionWarning = (brand, productSlug, masterVariantKey) => {
      if (process.client) {
        console.warn('Card.vue: Cannot construct URL', {
          brand,
          productSlug,
          masterVariantKey,
          masterProductSlug: masterProductSlug?.value,
          masterProduct: masterProduct?.value,
          productVariants: productVariants.value
        })
      }
    }

    const subCatSlug = computed(() => {
      // let categorySlugForUrl = categorySlug
      // TODO - all tilgifts to have /tilgift urls till infra creates the dynamic sub category mapping
      // const subCat = props.categoryTree?.find(category => masterProductCategories?.value?.includes(category?.id))
      // if (subCat !== undefined) {
      //   categorySlugForUrl = subCat?.slug
      // }
      const brand = productBrand?.value?.toLowerCase()
      // Use variantKey if available (for variants), otherwise use slug (for master products)
      // According to Java code, variantKey is set to slug for all products, but we check both for safety
      const masterVariantKey = masterProduct?.value?.variantKey
      const productSlug = masterVariantKey || masterProductSlug?.value
      if (!brand || !productSlug) {
        logUrlConstructionWarning(brand, productSlug, masterVariantKey)
        return { path: null }
      }
      const url = '/' + categorySlug + '/' + brand + '/' + productSlug
      return { path: url }
    })

    const trackArrowClick = (eventData) => {
      track('carouselArrowClick', { ...eventData, productDetails: null, variantDetails })
    }

    const gotoLegalTextSection = () => {
      // scroll down to the faq accordion panel and open the first one
      const legalTextSection = document.querySelector('.legal-text-box')
      window.scroll({
        top: legalTextSection.getBoundingClientRect().top + window.scrollY,
        behavior: 'smooth'
      });
    }

    const isVisible = ref(!!props.initialVisible)
    const onIntersect = (_entries, _observer, isIntersecting) => {
      if (isIntersecting) {
        isVisible.value = true
      }
    }

    // Media popup state for energy label and product sheet
    const showEnergyPopup = ref(false)
    const fullEnergyLabelUrl = ref('')
    const showProductDataSheetPopup = ref(false)
    const productDataSheetUrl = ref('')

    const openEnergyPopup = () => {
      const fullEnergyLabel = getAttributeValueIfPresent('full-energy-label')
      if (fullEnergyLabel) {
        fullEnergyLabelUrl.value = fullEnergyLabel
        showEnergyPopup.value = true
      }
    }

    const closeEnergyPopup = () => {
      showEnergyPopup.value = false
      fullEnergyLabelUrl.value = ''
    }

    const openProductDataSheetPopup = () => {
      const productDataSheet = getAttributeValueIfPresent('product-data-sheet')
      if (productDataSheet) {
        productDataSheetUrl.value = productDataSheet
        showProductDataSheetPopup.value = true
      }
    }

    const closeProductDataSheetPopup = () => {
      showProductDataSheetPopup.value = false
      productDataSheetUrl.value = ''
    }

    const getAttributeValueIfPresent = (attributeName) => {
      const attributes = variantDetails.productAttributes?.value || []
      const attribute = attributes.find(attr => attr.name === attributeName)
      const value = attribute?.value
      return value !== undefined && value !== null ? value : ''
    }

    // Find the energy efficiency feature from featureList
    const energyFeature = computed(() => {
      if (!featureList?.value) {
        return null
      }
      return featureList.value.find(feature => feature.name === 'EnergyEfficiency')
    })

    const isContentfulImage = (imageUrl) => {
      return imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('//images.ctfassets.net');
    }

    return {
      loadProductDetails,
      state,
      saveBackUrl,
      gotoLegalTextSection,
      campaign,
      categorySlug,
      subCatSlug,
      trackArrowClick,
      isVisible,
      onIntersect,
      formatPrice,
      productName,
      masterProductPrice,
      productBrand,
      productStorageVariants,
      masterProductLabels,
      masterProductImages,
      masterProductTimer,
      productFamilyColorVariants,
      masterProductSlug,
      masterVariantPricingDetails,
      chosenVariantCashPrice,
      // Media popup
      showEnergyPopup,
      fullEnergyLabelUrl,
      closeEnergyPopup,
      showProductDataSheetPopup,
      productDataSheetUrl,
      closeProductDataSheetPopup,
      openEnergyPopup,
      openProductDataSheetPopup,
      energyFeature,
      isContentfulImage
    }
  }
}
